import {
  COLOR_THEMES,
  saveColorTheme,
  saveTextScale,
  TEXT_SCALES,
  getSettings,
  saveSettings,
  addGoal,
  deleteGoalByTextMatch,
  markReflectionOfferedToday,
  getNotificationSettings,
  saveNotificationSettings,
  clearAIWelcomeCache,
  saveCustomThemePalette,
  updateUserPreferences,
  enqueueReminderMemory,
} from './storage';
import {
  scheduleNotification,
  cancelNotification,
  requestNotificationPermission,
  scheduleOneOffReminder,
} from './notifications';
import { buildCustomThemeFromColorInput } from './customThemePalette';

const normalizeKey = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, '');

const THEME_ALIASES = {
  dark: 'midnightblue',
  midnight: 'midnightblue',
  'midnightblue': 'midnightblue',
  navy: 'midnightblue',
  light: 'pure',
  white: 'pure',
  minimal: 'pure',
  purple: 'wisteria',
  lavender: 'wisteria',
  wisteria: 'wisteria',
  blue: 'skyblue',
  sky: 'skyblue',
  skyblue: 'skyblue',
  green: 'sage',
  sage: 'sage',
  forest: 'forestgreen',
  forestgreen: 'forestgreen',
  orange: 'sunset',
  sunset: 'sunset',
  sunrise: 'sunrise',
  yellow: 'sunrise',
  aubergine: 'aubergine',
  plum: 'aubergine',
  pure: 'pure',
  slate: 'slate',
  grey: 'slate',
  gray: 'slate',
};

const resolveThemeKey = (raw) => {
  const slug = String(raw || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  const direct = Object.keys(COLOR_THEMES).find((k) => k.toLowerCase() === slug);
  if (direct) return direct;
  const alias = THEME_ALIASES[slug];
  if (alias && COLOR_THEMES[alias]) return alias;
  const spaced = normalizeKey(raw).replace(/ /g, '');
  const alias2 = THEME_ALIASES[spaced];
  if (alias2 && COLOR_THEMES[alias2]) return alias2;
  return null;
};

const resolveTextScale = (raw) => {
  const k = String(raw || '').trim().toLowerCase();
  if (TEXT_SCALES[k]) return k;
  if (k === 'medium' || k === 'normal' || k === 'regular') return 'default';
  if (k === 'big' || k === 'larger') return 'large';
  if (k === 'extra' || k === 'extralarge' || k === 'xl') return 'xlarge';
  return null;
};

const parseReminderTime = (raw) => {
  const s = String(raw || '').trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

const PREF_KEYS = new Set([
  'coaching_style',
  'communication_style',
  'dislikes',
  'key_motivators',
]);

const parseReminderPayload = (raw) => {
  const src = String(raw || '').trim();
  const timeMatch = src.match(/(?:^|[,;]\s*)time\s*=\s*([0-2]?\d:[0-5]\d)\b/i);
  const textMatch = src.match(/(?:^|[,;]\s*)text\s*=\s*(.+)$/i);
  const time = String(timeMatch?.[1] || '').trim();
  const text = String(textMatch?.[1] || '').trim();
  if (!time) return null;
  return { time, text: text || 'Reminder from your RE-BUILD-ME coach.' };
};

/**
 * Apply structured commands from home / coach chat (see `buildChatPrompt` in proCoachMLX).
 * @param {Array<{ type: string, value: string }>} commands
 * @returns {Promise<{ applied: string[], errors: string[] }>}
 */
export const applyProChatCommands = async (commands) => {
  const applied = [];
  const errors = [];
  if (!Array.isArray(commands) || !commands.length) {
    return { applied, errors };
  }

  for (const cmd of commands) {
    const type = String(cmd?.type || '').toUpperCase();
    const value = String(cmd?.value || '').trim();
    if (!type || !value) continue;

    try {
      if (type === 'THEME') {
        const key = resolveThemeKey(value);
        if (!key) {
          errors.push(`Unknown theme: ${value}`);
          continue;
        }
        saveColorTheme(key);
        applied.push(`Theme → ${COLOR_THEMES[key]?.name || key}`);
        continue;
      }

      if (type === 'CUSTOM_THEME') {
        let variant = 'light';
        let colorPart = value;
        const dm = /^\s*dark\s*[:|]\s*(.+)$/i.exec(value) || /^\s*dark\s+(.+)$/i.exec(value);
        if (dm) {
          variant = 'dark';
          colorPart = dm[1].trim();
        }
        const built = buildCustomThemeFromColorInput(colorPart, value, variant);
        if (!built) {
          errors.push(`Could not build a theme from: ${value}`);
          continue;
        }
        saveCustomThemePalette(built);
        saveColorTheme('custom');
        applied.push(`Custom theme (${variant}) → ${built.label}`);
        continue;
      }

      if (type === 'TEXT_SCALE') {
        const scale = resolveTextScale(value);
        if (!scale) {
          errors.push(`Unknown text size: ${value}`);
          continue;
        }
        saveTextScale(scale);
        applied.push(`Text size → ${scale}`);
        continue;
      }

      if (type === 'NAME') {
        const name = value.slice(0, 80);
        if (!name) {
          errors.push('Empty name');
          continue;
        }
        const settings = getSettings();
        saveSettings({ ...settings, name });
        clearAIWelcomeCache();
        applied.push('Name updated');
        continue;
      }

      if (type === 'GOAL_ADD') {
        const text = value.slice(0, 200);
        if (!text) {
          errors.push('Empty goal');
          continue;
        }
        const settings = getSettings();
        if ((settings.goals || []).length >= 5) {
          errors.push('Goal list is full (max 5)');
          continue;
        }
        addGoal({ text });
        clearAIWelcomeCache();
        applied.push(`Goal added: ${text.slice(0, 40)}${text.length > 40 ? '…' : ''}`);
        continue;
      }

      if (type === 'GOAL_DELETE') {
        const needle = value.slice(0, 200);
        if (!needle) {
          errors.push('Empty goal delete text');
          continue;
        }
        const ok = deleteGoalByTextMatch(needle);
        if (!ok) {
          errors.push(`No goal matched: ${needle.slice(0, 60)}`);
          continue;
        }
        clearAIWelcomeCache();
        applied.push(`Goal removed: ${needle.slice(0, 40)}${needle.length > 40 ? '…' : ''}`);
        continue;
      }

      if (type === 'REFLECTION_OFFERED') {
        markReflectionOfferedToday();
        continue;
      }

      if (type === 'PREF_UPDATE') {
        const parts = value.match(/^([a-z_]+)\s*=\s*(.+)$/i);
        if (!parts) {
          errors.push('PREF_UPDATE format: key=value');
          continue;
        }
        const key = String(parts[1] || '').trim().toLowerCase();
        const prefValue = String(parts[2] || '').trim();
        if (!PREF_KEYS.has(key)) {
          errors.push(`Unknown preference key: ${key}`);
          continue;
        }
        if (!prefValue) {
          errors.push(`Preference value required for: ${key}`);
          continue;
        }
        updateUserPreferences({ [key]: prefValue });
        applied.push(`Preference learned: ${key.replace(/_/g, ' ')}`);
        continue;
      }

      if (type === 'DAILY_REMINDER') {
        const v = value.toLowerCase();
        const on = v === 'on' || v === 'true' || v === '1' || v === 'enabled' || v === 'yes';
        const off = v === 'off' || v === 'false' || v === '0' || v === 'disabled' || v === 'no';
        if (!on && !off) {
          errors.push(`Reminder: use on or off (got ${value})`);
          continue;
        }
        const cur = getNotificationSettings();
        if (on) {
          const granted = await requestNotificationPermission();
          if (!granted) {
            errors.push('Notification permission denied');
            continue;
          }
          saveNotificationSettings(true, cur.time);
          await scheduleNotification(cur.time);
        } else {
          saveNotificationSettings(false, cur.time);
          await cancelNotification();
        }
        applied.push(on ? 'Daily reminder on' : 'Daily reminder off');
        continue;
      }

      if (type === 'REMINDER_TIME') {
        const time = parseReminderTime(value);
        if (!time) {
          errors.push(`Invalid time (use HH:MM, e.g. 09:30)`);
          continue;
        }
        const cur = getNotificationSettings();
        saveNotificationSettings(cur.enabled, time);
        if (cur.enabled) await scheduleNotification(time);
        applied.push(`Reminder time → ${time}`);
        continue;
      }

      if (type === 'REMINDER') {
        const parsed = parseReminderPayload(value);
        if (!parsed) {
          errors.push('REMINDER format: time=HH:MM, text=...');
          continue;
        }
        const reminderId = `rem_${Date.now()}`;
        const schedule = await scheduleOneOffReminder({
          id: reminderId,
          time: parsed.time,
          text: parsed.text,
        });
        const mode = schedule.ok ? 'notification' : 'conversation_only';
        const saved = enqueueReminderMemory({
          id: reminderId,
          text: parsed.text,
          time: parsed.time,
          dueAtIso: schedule.dueAtIso,
          scheduleMode: mode,
        });
        if (mode === 'notification') {
          applied.push(`Reminder set for ${saved.time}`);
        } else {
          applied.push(`Reminder saved for next chat (${saved.time})`);
        }
        continue;
      }

      errors.push(`Unknown command: ${type}`);
    } catch (e) {
      errors.push(String(e?.message || e));
    }
  }

  return { applied, errors };
};
